/**
 * A STOP IN HER OWN WORDS, HELD FOR THE OFFICE, IN WORDS (ops #30, decided at ops #33's door).
 *
 * One copy of every sentence the screens say about a hold, so the Texts screen's band, her card on the
 * household page and the line under her text in a thread never say one hold two ways:
 *
 *   what was found     the phrase found in her text, as found and never as a quotation of her, and
 *                      what it asked: texts, or every channel
 *   the clock          from the days the server worked out in Florida, so a phone in another zone
 *                      reads the Thursday the tick will act on, on business days, Monday to Friday
 *   how it ended       confirmed, cleared, a stop nobody decided, or answered by her own stop
 *   what a press did   the sentence shown after a decision, and for a clear what still refuses her
 *                      texts, since a clear lifts nothing
 *   what it bars       beside the composer's Send, why nothing can be written to her, and where the
 *                      hold is decided
 *
 * Never the number: the hold keeps it in clear through a forget, and the server never sends it.
 */
import type { MarksHeld, StopHoldView } from './api.ts';
import { markGroups, markLine } from './components/DoNotContact.tsx';
import { formatDay, longDay, weekdayName } from './format.ts';
import { NOTIFICATION_KIND_LABELS } from './labels.ts';

/**
 * The gold pill on a hold, never coral: a decision waiting, like a rule that fired. The owners'
 * notice of one says it in the same words.
 */
export const HOLD_PILL_WORDS = NOTIFICATION_KIND_LABELS.opt_out_hold;

/** The pill on her text in the Texts list while its hold is open. */
export const MAY_BE_INSIDE_WORDS = 'A stop may be inside it';

/** Said small under every clock sentence. */
export const BUSINESS_DAYS_WORDS = 'Business days are Monday to Friday in Florida.';

/** "Thursday, September 17, 2026". */
const dayWithWeekday = (day: string): string => `${weekdayName(day)}, ${formatDay(day) ?? day}`;

/** The phrase found, under her whole text, or in place of it once a forget blanked it. */
export function foundWords(hold: StopHoldView, forgotten: boolean): string {
  return forgotten
    ? `Their text has been forgotten. The words found were '${hold.words}'.`
    : `The words found: '${hold.words}'.`;
}

/** What her words asked, by the channel they named. */
export function askedWords(hold: StopHoldView): string {
  return hold.wordsChannel === 'all'
    ? 'They ask to be left alone on every channel: texts, calls and email.'
    : 'They ask us to stop texting.';
}

/** Where an open hold's clock stands, in the words the issue asked for. */
export function holdClockWords(hold: StopHoldView): string {
  if (hold.suppressDue) {
    return 'Nobody decided in five business days, so it becomes a stop on every channel at the next tick.';
  }
  const becomes = `Unless somebody decides, it becomes a stop on every channel on ${dayWithWeekday(hold.suppressDueOn)}.`;
  if (hold.alertedOn !== null) {
    return `Both owners were told on ${dayWithWeekday(hold.alertedOn)}. ${becomes}`;
  }
  return `Texts to their number are refused while this waits. ${becomes} Both owners are told on ${longDay(hold.alertDueOn)} if nobody has decided by then.`;
}

/**
 * WHY NOTHING CAN BE WRITTEN TO HER WHILE A HOLD WAITS, beside the composer's Send (ops #32), in the
 * pill's words and the clock's, and where it is decided: in the band at the top of the Texts list where
 * the composer is on that list, and on the Texts list or on her card from her household's page, the
 * two places the decision is offered and the places the release refusal names. Never the server's
 * sentence, which keeps the hold's id and its day as a date in code for the row a refused text is
 * parked with.
 */
export function holdBarsTextsWords(onTextsList: boolean): string {
  const where = onTextsList
    ? 'Decide it first, under Stops waiting for a decision at the top of this list.'
    : 'Decide it first, on the Texts list or on their card.';
  return `${HOLD_PILL_WORDS}, and texts to their number are refused while it waits. ${where}`;
}

/** How an ended hold ended, by whom and on which day, or null while it is open. */
export function holdEndedWords(hold: StopHoldView): string | null {
  const on = hold.outcomeOn === null ? null : (formatDay(hold.outcomeOn) ?? hold.outcomeOn);
  const by = hold.outcomeByName ?? 'the office';
  switch (hold.outcome) {
    case null:
      return null;
    case 'confirmed':
      return `Confirmed as a stop by ${by}${on ? ` on ${on}` : ''}.`;
    case 'cleared':
      return `Cleared by ${by}${on ? ` on ${on}` : ''}: ${hold.outcomeNote ?? 'no reason kept'}`;
    case 'auto_suppressed':
      return `Became a stop on every channel${on ? ` on ${on}` : ''}, nobody having decided.`;
    case 'superseded':
      return 'Answered by their own stop.';
  }
}

/** What a confirmation did, said once it landed. */
export function confirmedWords(channel: StopHoldView['wordsChannel']): string {
  return channel === 'all'
    ? 'Marked. Nothing goes to them by text, call or email from now on.'
    : 'Marked. Nothing goes to them by text from now on.';
}

/**
 * WHAT A CLEAR DID: her texts go again, unless a mark beneath still refuses them, which a clear never
 * lifts, and then which, in the words her card uses for a mark.
 */
export function clearedWords(stillRefused: MarksHeld | null | undefined): string {
  const onTexts = markGroups(stillRefused).filter((group) => group.channels.includes('sms'));
  if (onTexts.length === 0) return 'Cleared. Texts to their number go again.';
  return `Cleared. Texts to their number are still refused: ${onTexts.map(markLine).join(' ')}`;
}
